/**
 * Shared sidebar footer component.
 * Shows user info row + theme/signout action row.
 * Accepts optional extraActions to inject additional icon buttons (e.g. admin shield).
 */

import { Link } from "react-router-dom";
import { useAuth } from "../../lib/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { LogOut, Moon, Sun } from "lucide-react";
import InsufficientBoostsBanner from "../InsufficientBoostsBanner";
import { publicRoutes } from "../../lib/routes";

interface SidebarFooterProps {
  /** Optional extra icon buttons rendered in the action row (after theme toggle, before sign-out). */
  extraActions?: React.ReactNode;
}

export function SidebarFooter({ extraActions }: SidebarFooterProps) {
  const { user, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="px-3 py-4 border-t border-border/30">
      <InsufficientBoostsBanner />
      {/* User info */}
      {user && (
        <Link
          to={publicRoutes.profile}
          className="nav-item flex items-center gap-3 px-3 py-2 mb-2 rounded-xl hover:bg-muted/50 transition-colors"
        >
          <div className="h-9 w-9 rounded-xl bg-nara-orange flex items-center justify-center shrink-0">
            <span className="text-nara-charcoal text-sm font-bold">
              {user.email?.charAt(0).toUpperCase() || "U"}
            </span>
          </div>
          <div className="footer-text flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">
              {user.displayName || user.email}
            </p>
          </div>
        </Link>
      )}
      <div className="footer-actions flex items-center gap-1 px-1">
        <button
          onClick={toggleTheme}
          className="p-2.5 hover:bg-muted/50 rounded-xl transition-colors"
          aria-label="Toggle theme"
        >
          {theme === "dark" ? (
            <Sun className="h-[1.1rem] w-[1.1rem] text-muted-foreground" />
          ) : (
            <Moon className="h-[1.1rem] w-[1.1rem] text-muted-foreground" />
          )}
        </button>
        {extraActions}
        <button
          onClick={() => signOut()}
          className="p-2.5 hover:bg-muted/50 rounded-xl transition-colors"
          title="Sign Out"
        >
          <LogOut className="h-[1.1rem] w-[1.1rem] text-muted-foreground" />
        </button>
      </div>
    </div>
  );
}
